import { InvoiceItem } from '@/pages/AddInvoice/components/InvoiceItemAccordion/InvoiceItemAccordion.types';
import { Contact } from '@/types/contacts';
import { ApiPaginatedResponse, ApiResponse } from './api';
import type { User } from './user';
import { MatchingRecord } from './matching';
import type { Transaction } from './bank';

export const PARSED_INVOICES_TABLE_KEY = 'parsed_invoices';
export const PARSED_INVOICES_FILTER_KEY = 'parsed_invoices_filter';

export const OTHER_DOCUMENTS_TABLE_KEY = 'other_documents';
export const OTHER_DOCUMENTS_FILTER_KEY = 'other_documents_filter';

export const DOCUMENTS_SALES_PURCHASES_INVOICES_TYPE = 'invoice';
export const LOCAL_TRAVEL_TICKET = 'local_travel_ticket';

export enum DocumentTypes {
  PURCHASE = 'purchase_invoice',
  SALE = 'sales_invoice',
  BANK_STATEMENT = 'bank_statement',
  BANK_MOVEMENT = 'bank_movement',
  OTHER = 'other',
  EMPLOYMENT_CONTRACT = 'employment_contract',
  CONTRACT_CONTRACTOR = 'contract_contractor',
  TSD_REPORT = 'tsd_report',
  OSS_REPORT = 'oss_report',
  VAT_REPORT = 'vat_report',
  BUSINESS_TRIP = 'business_trip_doc',
  PASSPORT_PIC = 'passport_pic'
}

export enum OtherDocumentTypes {
  OTHER = 'other',
  PASSPORT_PIC = 'passport_pic',
  COMPANY_DATA = 'company_data',
  CONTRACT = 'contract',
  COMPANY_REGISTRATION = 'company_registration',
  BUSINESS_TRIP_DOC = 'business_trip_doc',
  SCANNED_LETTER = 'scanned_letter',
  EMPLOYMENT_CONTRACT = 'employment_contract',
  COMPANY_MEMBER_RESIDENCE_PROOF = 'member_residence_proof',
  STATEMENT_PROFIT_LOSSES_ARCHIVE = 'statement_profit_losses_archive',
  BALANCE_SHEETS_ARCHIVE = 'balance_sheets_archive',
  GENERAL_LEDGER_ARCHIVE = 'general_ledger_archive',
  BANK_STATEMENTS_ARCHIVE = 'bank_statements_archive',
  ACCOUNTS_PAYABLE_RECEIVABLE_ARCHIVE = 'accounts_payable_receivable_archive'
}

export enum UploadOtherDocumentTypes {
  STATEMENT_PROFIT_LOSSES_ARCHIVE = 'statement_profit_losses_archive',
  BALANCE_SHEETS_ARCHIVE = 'balance_sheets_archive',
  GENERAL_LEDGER_ARCHIVE = 'general_ledger_archive',
  BANK_STATEMENTS_ARCHIVE = 'bank_statements_archive',
  ACCOUNTS_PAYABLE_RECEIVABLE_ARCHIVE = 'accounts_payable_receivable_archive',
  OTHER = 'other'
}

export enum DocumentStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  REJECTED = 'rejected',
  DELETED = 'deleted',
  APPROVED = 'approved'
}

export enum TransactionTypes {
  CREDIT = 'credit',
  DEBIT = 'debit',
  UNKNOWN = 'unknown',
  INTERNAL_MOVEMENTS = 'internal_movements'
}

export enum MatchedBy {
  TEAM = 'team',
  ACCOUNTANT = 'admin',
  AUTO = 'automatic',
  UNMATCHED = 'unmatched',
  CUSTOMER = 'customer'
}

export enum MatchedTo {
  SALE_INVOICE = 'sales_invoice',
  PURCHASE_INVOICE = 'purchase_invoice',
  CANCELED = 'canceled',
  NOT_MATCHED = 'not_matched'
}

export enum RejectionStatus {
  DETECTED = 'detected',
  VERIFICATION_REQUESTED = 'verification_requested',
  SOLVED = 'solved',
  CONFIRMED = 'confirmed'
}
export enum InvoiceTemplate {
  DEFAULT = 'default',
  MODERN = 'modern',
  BOLD = 'bold',
  TEKKIE = 'tekkie',
  ELEGANT = 'elegant',
  VIBRANT = 'vibrant'
}

export interface InvoiceDocumentItem {
  description: string;
  total: number;
  vat_amount: number;
  vat_percentage: number;
  price: number;
  quantity?: number;
  tax_amount?: number;
  tax_percentage?: number;
  isOpenInitialAccordion?: boolean;
  no_vat?: boolean;
}

export interface InvoiceDocument {
  rejection?: {
    reasons: string[];
    appeal: string;
    reviewed_by: string;
    status: RejectionStatus;
    non_business_transactions?: string[];
    non_business_transactions_populated?: Transaction[];
  };
  out_of_pocket: boolean;
  _id: string;
  name: string;
  company: string;
  path: string;
  type: DocumentTypes;
  mime_type: string;
  description: string;
  sha256: string;
  invoice_from_us: boolean;
  status: DocumentStatus;
  created: string;
  original_date: string;
  invoice_from_contractor: string;
  matching?: MatchingRecord[];
  invoice_data?: {
    financial_data: {
      subtotal: number;
      vat_amount: number;
      vat_percentage: number;
      total: number;
      currency: string;
      currency_rate: number;
    };
    from: {
      info: string;
      name: string;
      registry_number: string;
      address: string;
      vat: string;
      country: string;
    };
    to: {
      info: string;
      name: string;
      address: string;
      country: string;
      contact_id: string;
      registry_number?: string;
      vat?: string;
    };
    general_data: {
      description: string;
      invoice_number: string;
      issue_date: string;
      status: string | null;
      transaction_date: string;
      uploaded_by: string | null;
      uploaded_by_name: string | null;
      due_date: string;
      late_penalty: boolean;
      payment_instructions: string;
      pro_forma: boolean;
      penalty_rate?: string;
      penalty_interval?: string;
      invoice_template?: InvoiceTemplate;
    };
    items: InvoiceDocumentItem[];
  };
  contact?: string;
  contact_populated?: Contact;
  bank?: string;
  tag?: {
    _id: string;
    tag_source: string;
  };
  accounting_requests?: [
    {
      date: Date;
      request_type: string;
      extra_info: string;
    }
  ];
  reminder?: {
    created: string;
    text: string;
    frequency: string;
  };
  recurrent_invoice: RecurrentInvoiceData | null;
  local_travel_ticket?: boolean;
  travel?: string | null;
}

export interface OtherDocument {
  _id: string;
  name: string;
  company: string;
  path: string;
  type: OtherDocumentTypes;
  mime_type: string;
  description: string;
  sha256: string;
  invoice_from_us: boolean;
  status: DocumentStatus;
  created: string;
  original_date: string;
  invoice_from_contractor: string;
  contact?: string;
  contact_populated?: Contact;
  bank?: string;
  tag?: {
    _id: string;
    tag_source: string;
  };
  reminder?: {
    created: string;
    text: string;
    frequency: string;
  };
}

export interface RecurrentInvoiceData {
  _id?: string;
  invoice_reference?: string;
  active: boolean;
  frequency: string;
  next_invoice_date: string;
  last_invoice_date: string;
  customer_notification?: {
    enabled: boolean;
    method: string;
  };
  contact_notification?: {
    enabled: boolean;
    method: string;
  };
}

export interface CreateInvoiceData {
  contact: string;
  invoice_number: string;
  subject: string;
  issue_date: string;
  due_date: string;
  late_penalty: boolean;
  payment_instructions: string;
  is_proforma: boolean;
  currency: string;
  total: string;
  items: InvoiceItem[];
  recurrent_invoice?: RecurrentInvoiceData | null;
}

export type UpdateMultipleDocumentsData = {
  data: {
    doc_id: string;
    updates: Record<string, any>;
  }[];
};

export type UpdateMultipleDocumentsTypeData = {
  data: {
    doc_id: string;
    updates: {
      name: string;
      type: DocumentTypes;
    };
  }[];
};

export type GetAllDocumentsByDateAndTypeResponse = ApiResponse<{
  docs: InvoiceDocument[];
}>;

export type GetPaginatedDocumentsByDateAndTypeResponse = ApiPaginatedResponse<{
  docs: InvoiceDocument[];
}>;

export type GetPaginatedOtherDocumentsByDateAndTypeResponse = ApiPaginatedResponse<{
  docs: OtherDocument[];
}>;

export type GetPaginatedDocumentsByContactId = ApiPaginatedResponse<{
  docs: InvoiceDocument[];
}>;

export type GetDocumentByIdResponse = ApiResponse<{
  document?: InvoiceDocument;
}>;

export type InvoicePreviewResponse = ApiResponse<{
  document: string;
}>;

export type CreateInvoiceResponse = ApiResponse<{
  contact: Contact;
  document: InvoiceDocument;
}>;

export enum CSVImportFriendlyMessageTypes {
  ERROR = 'error',
  INSTRUCTION = 'instruction',
  INFO = 'info'
}

export interface CSVImportFriendlyMessages {
  type: CSVImportFriendlyMessageTypes;
  message: string;
}

export type VerifyCSVSalesDocumentResponse = ApiResponse<{
  validations: {
    headers: boolean;
    rows: boolean;
    sales: number;
  };
  friendlyMessages: CSVImportFriendlyMessages[];
}>;

export type UploadCSVSalesDocumentResponse = ApiResponse<{
  successful: number; // number of successful sales invoices uploaded
  unsuccessful: string; // url for the download report
}>;

export type UpdateMultipleDocumentsResponse = ApiResponse<{
  documents: InvoiceDocument[];
}>;

interface Rejection {
  reasons: string[];
}

export interface DocumentBase {
  _id: string;
  name: string;
  company: string;
  path: string;
  type: string;
  mime_type: string;
  description: string;
  sha256: string;
  status: string;
  created: string;
  original_date: string;
}

export interface Document extends DocumentBase {
  rejection: Rejection;
  out_of_pocket: boolean;
  description: string;
  invoice_from_us: boolean;
  status: DocumentStatus;
  uploaded_by_user: string;
  bank: string;
  accounting_requests: any[]; // TODO: Check this type
}

export type UpdateDocumentResponse = ApiResponse<{
  document: Document;
}>;

export interface UploadDocumentRequest {
  filename: string;
  filedata: string;
  currentUser: User;
  type: DocumentTypes;
  forceUpload?: boolean;
  bankId?: string | null;
  selectedUser?: string | null;
}

export interface UploadDocumentResponse {
  document: Document;
  error: boolean;
  error_code: string;
}

export interface ConvertToPurchaseInvoiceRequest {
  documentId: string;
}

export enum MimeTypes {
  APPLICATION_PDF = 'application/pdf',
  APPLICATION_MSWORD = 'application/msword',
  TEXT_PLAIN = 'text/plain',
  WPS_PDF = 'application/wps-office.pdf',
  APPLICATION_VND_OPENXMLFORMATS_OFFICEDOCUMENT_WORDPROCESSINGML_DOCUMENT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  APPLICATION_VND_MS_EXCEL = 'application/vnd.ms-excel',
  APPLICATION_VND_OPENXMLFORMATS_OFFICEDOCUMENT_SPREADSHEETML_SHEET = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  TEXT_CSV = 'text/csv',
  IMAGE_JPEG = 'image/jpeg',
  IMAGE_PNG = 'image/png',
  VIDEO_MP4 = 'video/mp4',
  VIDEO_QUICKTIME = 'video/quicktime',
  VIDEO_WEBM = 'video/webm',
  VIDEO_X_MSVIDEO = 'video/x-msvideo',
  VIDEO_X_MATROSKA = 'video/x-matroska',
  VIDEO_X_FLV = 'video/x-flv',
  VIDEO_X_MS_WMV = 'video/x-ms-wmv'
}

export enum FileExtensions {
  PDF = '.pdf',
  DOC = '.doc',
  TXT = '.txt',
  DOCX = '.docx',
  JPEG = '.jpeg',
  JPG = '.jpg',
  PNG = '.png',
  CSV = '.csv',
  XLS = '.xls',
  XLSX = '.xlsx',
  MP4 = '.mp4',
  MOV = '.mov',
  WEBM = '.webm',
  AVI = '.avi',
  MKV = '.mkv',
  FLV = '.flv',
  WMV = '.wmv'
}

export interface GetContractorInvoicesPaginatedRequest {
  page: number;
  limit: number;
  contractorId: string;
  filters?: Object;
}

export type GetContractorInvoicesPaginatedResponse = {
  invoices: ApiPaginatedResponse<{
    docs: InvoiceDocument[];
  }>;
};

export interface ReplaceDocumentRequest {
  filename: string;
  filedata: string;
  mimeType: string;
  documentId: string;
}

export interface AppealRejectionRequest {
  documentId: string;
  reason: string;
}

export interface UploadMultipleDocumentsRequest {
  file: File;
  type:
    | DocumentTypes
    | UploadOtherDocumentTypes
    | typeof DOCUMENTS_SALES_PURCHASES_INVOICES_TYPE
    | typeof LOCAL_TRAVEL_TICKET;
  force?: boolean;
  lastMonth?: boolean;
  bankId?: string | null;
  travelId?: string | null;
  isLocalTravelTicket?: boolean;
}

export enum UploadSaleInvoiceOptions {
  CURRENT_MONTH = 'current_month',
  LAST_MONTH = 'last_month'
}

export enum UploadOtherDocumentsOptions {
  CURRENT_MONTH = 'current_month',
  LAST_MONTH = 'last_month'
}

export enum PenaltyInterval {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}
export interface AreCSVColumnsValidParams {
  requiredColumns: string[];
  csvColumns: string[];
  caseSensitive?: boolean;
}

export enum AmazonImportCSVError {
  CSV_HEADERS_MALFORMED = 'csv_headers_malformed',
  ERROR_PROCESSING_THE_CSV_FILE = 'error_processing_the_csv_file',
  ERROR_CSV_ROW_NUMBER_MALFORMED = 'error_csv_row_number_malformed',
  ERROR_PROCESSING_CSV_ROW_NUMBER = 'error_processing_csv_row_number'
}

export interface TaxSchema {
  [key: string]: {
    total_vat: number;
    subtotal: number;
    total: number;
    vat: number;
    currency: string;
    invoice_number: string;
    amount?: number;
  };
}

export interface AmazonSalesData {
  invoice_number: string;
  amount: number;
}

export interface NormalizedAmazonTransaction {
  due_date: string;
  issue_date: string;
  invoice_number: string;
  vat: number;
  currency: string;
  total_vat: number;
  total?: number;
  subtotal?: number;
  buyer_country?: string;
  items: {
    description: string;
    vat: number;
    total?: number;
    total_vat: number;
    subtotal?: number;
    currency: string;
  }[];
}

export type CreateSaleInvoiceFromAmazonCSVRowDataResponse = ApiResponse<{
  created: boolean;
}>;

export interface AdvancedSearchFormValues {
  invoice_number: string;
  counterparty: string;
  category: string;
  invoice_total: string;
  time_range: {
    start_date: Date | null;
    end_date: Date | null;
  };
  document_name: string;
  document_id?: string;
}

export interface CalculateLatePenaltyCostProps {
  latePenalty?: boolean;
  penaltyInterval?: string;
  penaltyRate?: string;
  items: InvoiceDocumentItem[];
  dueDate?: string | Date;
}

interface UpdateInvoiceData extends CreateInvoiceData {
  late_penalty_amount: number | null;
  penalty_interval?: string | null;
  penalty_rate?: string | null;
  pro_forma: boolean;
}

export interface UpdateInvoiceRequest {
  documentId: string;
  invoice: UpdateInvoiceData;
}

export interface UploadDocumentRequestParams {
  data: string;
  name: string;
  description: string;
  type: DocumentTypes;
  force?: boolean;
  bank_id?: string;
}

export interface UpdateDocumentTagRequest {
  documentId: string;
  tagId: string;
}

export interface EnableInvoiceReminderRequest {
  documentId: string;
  reminderData: {
    frequency: string;
    text: string;
  };
}

export interface RemoveInvoiceReminderRequest {
  documentId: string;
}

export interface GetPaginatedDocumentsByDateRequest {
  year?: number;
  month?: number;
  type?: DocumentTypes[];
  page: number;
  limit: number;
  filters?: Object;
  advancedFilters?: Object;
  search?: string;
  sort?: string | null;
}

export interface GetPaginatedOtherDocumentsRequest {
  type?: Object;
  page: number;
  limit: number;
  filters?: Object;
  search?: string;
}

export interface GetDocumentsByIdsRequest {
  documentIds: string[];
  populate?: boolean;
}

export type GetDocumentsByIdsResponse = ApiResponse<{
  documents: InvoiceDocument[];
}>;

export interface EuVatRate {
  country: string;
  code: string;
  standardRate: number;
}
export interface VatValidationError {
  data: {
    isInvalidVatError?: boolean;
    message?: string;
  };
}

export enum UpdateVatNumberOption {
  CHANGE = 'change',
  REMOVE = 'remove'
}

export interface ExportDocumentsRequest {
  documentType: string;
  format: string;
  period?: string;
  advancedFilters?: AdvancedSearchFormValues | null;
  periodMonth?: number;
  periodYear?: number;
  documentSearch?: string;
  matchingFilter?: string[];
  statusFilter?: string[];
}
